/**
 * 단순 문자열 자동완성 — distinct 후보 리스트 + 직접 입력 동시 지원.
 *
 * 동작:
 *   - input 자체는 자유 텍스트 입력
 *   - focus 또는 typing 시 dropdown 열기
 *   - dropdown 맨 위: "✍ 직접 입력: '<현재 값>'" (현재 텍스트 그대로 사용)
 *   - 그 아래: 후보 중 부분일치 항목 (max 8개)
 *   - 항목 클릭 → onChange(item) + 닫기
 *   - 직접 입력 클릭 → 그냥 닫기 (현재 input 값 유지)
 *   - 외부 클릭 → 닫기
 */

import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  candidates: string[];
  placeholder?: string;
  className?: string;
  /** 후보 목록 머리 글자 (기본 「영업진행에서 등록된 항목」) — 후보가 다른 곳에서 올 때 (v2.293.0 · 견적서 참조 = 거래처 담당자 주소록) */
  heading?: string;
  /** 맞는 후보가 없을 때 글자 (기본 「일치하는 항목 없음 — 위 [직접 입력] 으로 추가」) */
  emptyText?: string;
  /** 보여 줄 후보 수 (기본 8) */
  max?: number;
}

export function SimpleAutocomplete({
  value,
  onChange,
  candidates,
  placeholder,
  className,
  heading = '영업진행에서 등록된 항목',
  emptyText = '일치하는 항목 없음 — 위 [직접 입력] 으로 추가',
  max = 8,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return candidates.slice(0, max);
    return candidates.filter((c) => c.toLowerCase().includes(q)).slice(0, max);
  }, [value, candidates, max]);

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="ui-input w-full"
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 top-full mt-0.5 max-h-72 overflow-y-auto bg-white border border-slate-300 rounded shadow-lg">
          {/* 직접 입력 옵션 — 항상 맨 위 */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full text-left px-2 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 border-b border-slate-200 font-medium text-blue-800"
          >
            ✍ 직접 입력{value.trim() ? ` : "${value}"` : ''}
          </button>
          {matches.length > 0 ? (
            <>
              <div className="px-2 py-1 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-200 sticky top-0">
                {heading} ({matches.length}개)
              </div>
              {matches.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    onChange(m);
                    setOpen(false);
                  }}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 border-b border-slate-100 last:border-b-0 text-slate-800"
                >
                  {m}
                </button>
              ))}
            </>
          ) : (
            <div className="px-2 py-3 text-center text-xs text-slate-500">
              {emptyText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
